import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Star,
  Zap,
  Target,
  Brain,
  CheckCircle,
  AlertCircle,
  Lightbulb
} from 'lucide-react';
import { EvaluationBar } from './EvaluationBar';
import type { PositionAnalysis } from '@/lib/stockfish-engine';

interface TeenFriendlyAnalysisPanelProps {
  analysis: PositionAnalysis | null;
  isAnalyzing: boolean;
  hasAttemptedAnalysis?: boolean;
  currentDepth?: number;
  onSaveAnalysis?: () => void;
  onExportPgn?: () => void;
}

export function TeenFriendlyAnalysisPanel({ 
  analysis, 
  isAnalyzing,
  hasAttemptedAnalysis = false,
  currentDepth,
  onSaveAnalysis,
  onExportPgn 
}: TeenFriendlyAnalysisPanelProps) {
  
  const getPositionMessage = (evaluation: number): { message: string; color: string; icon: React.ReactNode; bgColor: string } => {
    const absEval = Math.abs(evaluation);
    
    if (Math.abs(evaluation) > 900) {
      const mateIn = Math.abs(Math.round((1000 - Math.abs(evaluation))));
      return evaluation > 0 
        ? { 
            message: `Checkmate in ${mateIn}! 🎉`, 
            color: 'text-teen-green', 
            icon: <CheckCircle className="h-5 w-5" />,
            bgColor: 'bg-teen-green-bright/20 border-teen-green/30'
          }
        : { 
            message: `Defend against mate in ${mateIn}!`, 
            color: 'text-teen-red', 
            icon: <AlertCircle className="h-5 w-5" />,
            bgColor: 'bg-teen-red-bright/20 border-teen-red/30'
          };
    }
    
    if (absEval > 300) {
      return evaluation > 0 
        ? { 
            message: "White has a crushing advantage! 💪", 
            color: 'text-teen-green', 
            icon: <TrendingUp className="h-5 w-5" />,
            bgColor: 'bg-teen-green-bright/20 border-teen-green/30'
          }
        : { 
            message: "Black has a big advantage - tough spot! 💪", 
            color: 'text-teen-red', 
            icon: <TrendingDown className="h-5 w-5" />,
            bgColor: 'bg-teen-red-bright/20 border-teen-red/30'
          };
    }
    
    if (absEval > 150) {
      return evaluation > 0 
        ? { 
            message: "White advantage - great position! ⭐", 
            color: 'text-teen-blue', 
            icon: <Star className="h-5 w-5" />,
            bgColor: 'bg-teen-blue-bright/20 border-teen-blue/30'
          }
        : { 
            message: "Black advantage - time to be careful! ⚠️", 
            color: 'text-teen-orange', 
            icon: <AlertCircle className="h-5 w-5" />,
            bgColor: 'bg-teen-orange-bright/20 border-teen-orange/30'
          };
    }
    
    if (absEval > 50) {
      return evaluation > 0 
        ? { 
            message: "White has a small advantage 👍", 
            color: 'text-teen-cyan', 
            icon: <TrendingUp className="h-5 w-5" />,
            bgColor: 'bg-teen-cyan-bright/20 border-teen-cyan/30'
          }
        : { 
            message: "Black has a slight advantage ⚡", 
            color: 'text-teen-purple', 
            icon: <TrendingDown className="h-5 w-5" />,
            bgColor: 'bg-teen-purple-bright/20 border-teen-purple/30'
          };
    }
    
    return { 
      message: "Even game! Perfect balance ⚖️", 
      color: 'text-foreground', 
      icon: <Minus className="h-5 w-5" />,
      bgColor: 'bg-muted/50 border-muted'
    };
  };

  const formatEvaluation = (evaluation: number): { text: string; percentage: number } => {
    if (Math.abs(evaluation) > 900) {
      const mateIn = Math.abs(Math.round((1000 - Math.abs(evaluation))));
      return {
        text: evaluation > 0 ? `+M${mateIn}` : `-M${mateIn}`,
        percentage: evaluation > 0 ? 100 : 0
      };
    }
    
    // Convert centipawns to percentage
    const clampedEval = Math.max(-1000, Math.min(1000, evaluation));
    const percentage = 50 + (50 * clampedEval) / (200 + Math.abs(clampedEval));
    
    return {
      text: evaluation > 0 ? `+${(evaluation / 100).toFixed(1)}` : (evaluation / 100).toFixed(1),
      percentage: Math.max(0, Math.min(100, percentage))
    };
  };

  const getMoveRating = (evaluation: number): { stars: number; label: string } => {
    const absEval = Math.abs(evaluation);
    
    if (absEval > 300) return { stars: 3, label: "Excellent!" };
    if (absEval > 150) return { stars: 2, label: "Good move" };
    if (absEval > 50) return { stars: 1, label: "Okay" };
    return { stars: 0, label: "Perfect balance" };
  };

  const renderStars = (count: number) => {
    return Array.from({ length: 3 }, (_, i) => (
      <Star 
        key={i} 
        className={`h-4 w-4 ${
          i < count 
            ? 'text-teen-gold fill-teen-gold' 
            : 'text-muted-foreground/30'
        }`} 
      />
    ));
  };

  return (
    <div className="space-y-4">
      {/* Main Analysis Card - Teen Friendly */}
      <Card className="border-2 shadow-lg">
        <CardContent className="p-6 space-y-6">
          {/* Header with Coach Avatar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                <Brain className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Chess Coach</h3>
                <p className="text-sm text-muted-foreground">
                  {isAnalyzing 
                    ? `Analyzing... ${currentDepth && currentDepth > 0 ? `Depth ${currentDepth}/12` : 'Starting...'}`
                    : 'Ready to help!'}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              {onSaveAnalysis && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onSaveAnalysis}
                  className="h-9 px-3"
                >
                  Save
                </Button>
              )}
              {onExportPgn && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onExportPgn}
                  className="h-9 px-3"
                >
                  Export
                </Button>
              )}
            </div>
          </div>

          {/* Position Evaluation Card - Always Show */}
          <div className="space-y-4">
            {(() => {
              // Use analysis if available, otherwise show neutral evaluation
              const currentEval = analysis?.evaluation ?? 0;
              const evalData = formatEvaluation(currentEval);
              const positionData = getPositionMessage(currentEval);
              
              return (
                <div className={`p-4 rounded-xl border-2 ${positionData.bgColor} ${
                  isAnalyzing ? 'opacity-75 animate-pulse' : ''
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {positionData.icon}
                      <span className={`font-bold text-lg ${positionData.color}`}>
                        {isAnalyzing ? 'Analyzing position...' : positionData.message}
                      </span>
                    </div>
                    <div className={`font-mono text-xl font-bold ${positionData.color}`}>
                      {isAnalyzing && !analysis ? '...' : evalData.text}
                    </div>
                  </div>
                  
                  {/* Enhanced Evaluation Bar - Always Visible */}
                  <div className="space-y-2">
                     <EvaluationBar 
                       evaluation={currentEval} 
                       className="h-3 rounded-lg shadow-sm"
                       showNumbers={false}
                       showPlayerLabels={false}
                     />
                     <div className="flex justify-between text-sm font-semibold mt-2">
                       <span className="text-foreground">Black: {(100 - evalData.percentage).toFixed(0)}%</span>
                       <span className="text-foreground">White: {evalData.percentage.toFixed(0)}%</span>
                     </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Best Move Display - Always show once analysis has started */}
          {hasAttemptedAnalysis && (
            <div className="p-4 bg-teen-blue-bright/10 border-2 border-teen-blue/30 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-teen-blue" />
                  <span className="font-bold text-teen-blue">Best Move</span>
                </div>
                {analysis?.bestMove && (() => {
                  const rating = getMoveRating(analysis?.evaluation ?? 0);
                  return (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">{renderStars(rating.stars)}</div>
                      <span className="text-sm font-medium text-teen-gold">{rating.label}</span>
                    </div>
                  );
                })()}
              </div>
              <div className="font-mono text-2xl font-bold text-teen-blue">
                {analysis?.bestMove ? (
                  analysis.bestMove
                ) : isAnalyzing ? (
                  <div className="flex items-center gap-2 text-base">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-teen-blue border-t-transparent" />
                    <span>Thinking...</span>
                    {currentDepth !== undefined && currentDepth > 0 && (
                      <span className="text-sm text-muted-foreground">Depth {currentDepth}/12</span>
                    )}
                  </div>
                ) : (
                  <span className="text-base text-muted-foreground">
                    Eval: {(analysis?.evaluation ?? 0) > 0 ? '+' : ''}{((analysis?.evaluation ?? 0) / 100).toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Alternative Moves - Always show once analysis has started */}
          {hasAttemptedAnalysis && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-teen-orange" />
                <span className="font-bold text-teen-orange">Other Good Moves</span>
              </div>
              
              <div className="space-y-2">
                {/* Show engine variations if available */}
                {analysis?.variations && Array.isArray(analysis.variations) && analysis.variations.length > 0 ? (
                  analysis.variations.slice(0, 3).map((v, idx) => {
                    const rating = getMoveRating(v.evaluation);
                    return (
                      <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border-2 ${
                        idx === 0 
                          ? 'bg-teen-green-bright/10 border-teen-green/30' 
                          : 'bg-muted/30 border-muted/40'
                      }`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 ${
                            idx === 0 ? 'bg-teen-green text-white' : 'bg-muted text-foreground'
                          } rounded-full flex items-center justify-center text-sm font-bold`}>
                            {idx + 1}
                          </div>
                          <div>
                            <div className="font-mono text-base font-bold">
                              {Array.isArray(v.pv) ? v.pv[0] : v.move}
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex gap-1">{renderStars(rating.stars)}</div>
                              <span className="text-xs text-muted-foreground">{rating.label}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : isAnalyzing ? (
                  <div className="p-4 bg-muted/30 rounded-lg text-center flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-primary border-t-transparent" />
                    <p className="text-sm text-muted-foreground">
                      Calculating alternatives...
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">
                      {analysis?.evaluation !== undefined 
                        ? 'Single best move found' 
                        : 'Waiting for analysis...'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Loading State */}
          {isAnalyzing && (
            <div className="p-6 bg-teen-purple-bright/10 border-2 border-teen-purple/30 rounded-xl text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Zap className="h-5 w-5 text-teen-purple animate-pulse" />
                <span className="font-bold text-teen-purple">Chess Coach is thinking...</span>
              </div>
              <p className="text-sm text-muted-foreground">Finding the best moves for you!</p>
            </div>
          )}

          {/* No Analysis State */}
          {!analysis && !isAnalyzing && (
            <div className="text-center py-8">
              <Brain className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
              <h4 className="font-bold text-lg mb-2">Ready to Analyze!</h4>
              <p className="text-sm text-muted-foreground">Make a move and I'll help you understand the position</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}